import * as React from 'react';
import { saveAs } from 'file-saver';
import { AdminSettingsSection, Button } from 'src/components';
import useModal from 'src/hooks/useModal';
import { useAlerts } from 'src/common/AlertManager';
import { useMigrationBrokenAssetsMutation } from 'src/api/mutations/migrationMutations';
import { MigrationOverview } from 'src/types/TMigration';
import CheckUrlsModal from './CheckUrlsModal';
import CopyLibraryModal from './CopyLibraryModal';
import RunReport from './RunReport';
import { Note, Pill, SegmentBar, Stat, errorText, timeAgo } from './parts';
import styles from './styles.module.scss';

/**
 * "Getting your images across" — progress, and the two things you can do about it.
 *
 * The headline this section has to land in one sentence is that **the customer
 * does not have to do anything here**. With on-demand fetching on, images copy
 * themselves as visitors ask for them and the library moves over by itself.
 * Both buttons are optional: one copies everything up front instead of waiting,
 * the other is a safety check that changes nothing at all.
 *
 * Both open dialogs, matching how every other one-off action in the dashboard
 * works. Progress stays on the page, so closing a dialog never hides a copy
 * that is still running.
 */

type Props = {
  overview: MigrationOverview;
};

/** Plain wording for the reasons an arrived image cannot be served. The two
 *  causes need opposite actions, which is exactly why the screen no longer
 *  guesses at one of them. */
const BROKEN_REASON_COPY: Record<string, string> = {
  not_downloaded:
    'We could not download these from your provider — usually a private or deleted image, or one that needs your API key',
  // The dashboard says what happened, not which format caused it. Naming AVIF
  // here read as a contradiction of the feature list — we DELIVER AVIF happily,
  // we just cannot read one as a source file — and it is detail for the CSV,
  // not for a settings page.
  unreadable: 'We could not read the image file',
  other: 'Something else went wrong while processing these',
};

/** Shorter wording for the CSV, where the cell is read next to a path. */
const BROKEN_REASON_CSV: Record<string, string> = {
  not_downloaded: 'could not download from provider',
  unreadable: 'could not read the image file (an AVIF original is the usual cause)',
  other: 'processing failed',
};

export const MovingSection: React.FC<Props> = ({ overview }) => {
  const { showModal } = useModal();
  const { showAlert } = useAlerts();
  const fetchBroken = useMigrationBrokenAssetsMutation();

  /**
   * Download the affected paths rather than printing them.
   *
   * The list was rendered inline, which is fine for the sixteen on a test
   * account and unusable for the thousands a real migration can produce — a
   * settings page is not a place to dump a file listing. A CSV is also what
   * somebody would want anyway: it goes straight into the spreadsheet they are
   * already using to track the move.
   */
  const downloadBroken = async () => {
    try {
      const list = await fetchBroken.mutateAsync();
      const header = 'path,reason\n';
      const body = list.assets
        .map((b) => `"${b.path.replace(/"/g, '""')}","${BROKEN_REASON_CSV[b.reason] || b.reason}"`)
        .join('\n');
      saveAs(new Blob([header + body], { type: 'text/csv;charset=utf-8' }), 'images-that-cannot-be-used.csv');
      if (list.truncated) {
        showAlert('The list was very long, so the download holds the first 10,000 images.');
      }
    } catch (err) {
      showAlert(errorText(err, 'Could not build the list'));
    }
  };

  const { aliased, ready, processing, broken, last_copied_at: lastCopied } = overview.assets;
  const active = overview.active_run;
  const lastRun = overview.runs[0];
  const started = aliased > 0;

  const brokenReasons = Object.entries(overview.assets.broken_reasons || {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <AdminSettingsSection title="Getting your images across">
      <p className={styles.sectionLead}>
        Your images copy over on their own — the first time a visitor asks for one, we fetch it from your
        old provider, show it, and keep a copy. Over a few days your whole library moves without you
        doing anything. The buttons below are shortcuts, not steps you have to take.
      </p>

      {started ? (
        <>
          <div className={styles.statRow}>
            <Stat value={aliased} label="Images here" hint="Images of yours that now live on FileSpin." />
            <Stat tone="good" value={ready} label="Ready to serve" />
            <Stat
              tone={processing ? 'warn' : 'neutral'}
              value={processing}
              label="Still processing"
              hint="Arrived, not ready yet. These show a placeholder that fixes itself."
            />
            <Stat
              tone={broken ? 'bad' : 'neutral'}
              value={broken}
              label="Cannot be used"
              hint="Arrived, but we cannot serve them — see the reasons below."
            />
          </div>
          <SegmentBar
            className={styles.progressBar}
            segments={[
              { value: ready, tone: 'good', label: 'ready' },
              { value: processing, tone: 'warn', label: 'processing' },
              { value: broken, tone: 'bad', label: 'unusable' },
            ]}
          />
          {lastCopied && <p className={styles.rowHelp}>Last image arrived {timeAgo(lastCopied)}.</p>}
        </>
      ) : (
        <Note>Nothing has come across yet. Images will start arriving as soon as your URLs point here.</Note>
      )}

      {/* Never folded into any other number, and never guessed at. There are two
          quite different causes — a download that failed at the provider, and a
          file we downloaded but cannot decode — and they need opposite actions
          from the customer, so the reasons are reported rather than assumed. */}
      {broken > 0 && (
        <Note tone="warn">
          <strong>
            {broken} {broken === 1 ? 'image' : 'images'} cannot be used.
          </strong>{' '}
          These will not show on your site.
          {brokenReasons.length > 0 && (
            <table className={styles.table}>
              <tbody>
                {brokenReasons.map(([reason, count]) => (
                  <tr key={reason}>
                    <td>{BROKEN_REASON_COPY[reason] || reason}</td>
                    <td className={styles.tableNum}>{count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className={styles.sectionActions}>
            <Button mode="secondary" outlined onClick={downloadBroken} loading={fetchBroken.isLoading}>
              Download the list (CSV)
            </Button>
          </div>
        </Note>
      )}

      <div className={styles.toolRow}>
        <div className={styles.tool}>
          <div>
            <span className={styles.toolName}>Copy everything now</span>
            <span className={styles.rowHelp}>
              Bring your whole library across in one go instead of waiting for visitors to ask for it.
              Good for images nobody has requested in a while.
            </span>
          </div>
          <Button
            mode="secondary"
            outlined
            onClick={() => showModal(<CopyLibraryModal overview={overview} />)}
          >
            Copy library…
          </Button>
        </div>

        <div className={styles.tool}>
          <div>
            <span className={styles.toolName}>
              Check your URLs first <Pill tone="good">Safety check</Pill>
            </span>
            <span className={styles.rowHelp}>
              Paste some image URLs from your website and we tell you exactly what each one would look
              like after the move. Nothing is changed — it is safe to run before you decide anything.
            </span>
          </div>
          <Button mode="secondary" outlined onClick={() => showModal(<CheckUrlsModal />)}>
            Check URLs…
          </Button>
        </div>
      </div>

      {/* A copy in flight is shown on the page, not only inside the dialog — the
          customer starts one and then closes the window, and progress nobody
          can see is progress nobody trusts. */}
      {active && <RunReport run={active} />}
      {!active && lastRun && lastRun.status !== 'done' && <RunReport run={lastRun} />}
    </AdminSettingsSection>
  );
};

export default MovingSection;
